import React, { useEffect, useMemo, useState } from "react";
import type { Control } from "react-hook-form";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../Account/Register/Register.style";
import { COLORS } from "../../../../shared/theme/colorConfig";
import { configs } from "../../../../shared/config/config";

// One row of the Disposed of Asset Affidavit's 3-row table. Captured in the
// dedicated `disposedAssets` sub-step that only renders when assetsGivenAway
// === true. All four fields are required per row (yup-enforced). Capped at 3
// to match the PDF's 3 row slots; overflow goes in the explanation field.
export interface DisposedAssetRow {
  type: string;
  fairMarketValue: string;
  actualAmountReceived: string;
  dateOfDisposal: string;
}

// Per-asset cash value + interest rate. For NON-account assets (real estate,
// crypto, cash on hand, ...) this is captured directly, one per asset. For
// account-bearing assets it is the server-and-client-derived ROLLUP across that
// asset's `assetAccounts` entries: cashValue = Σ, interestRate = cash-weighted
// blended rate, institution / account = joined. Either way this is the single
// shape every downstream consumer reads (HOTMA total, the Asset Certification's
// Section 1 columns, the TAA Supplemental §5 row). Strings preserve raw input;
// parsed to numbers when summing / on the backend.
export interface AssetValueDetail {
  cashValue: string;
  interestRate: string;
  institution?: string;
  account?: string;
  // Explicitly-declared annual income for assets whose income is NOT cash x rate:
  // dividends/distributions for stocks/bonds/brokerage/capital investments, and
  // net rental income for real estate. Captured per asset key (not per account)
  // since Form 24-W §5 reports one income figure per asset row. Absent for deposit
  // assets (income there is derived as cashValue x interestRate) and for assets
  // that produce no countable income. See assetAnnualIncome() for the resolver.
  annualIncome?: string;
}

// One account / holding within an account-bearing asset type — a single bank
// account, brokerage account, or insurance policy. A household can hold several
// of the same type (e.g. two checking accounts at two banks), so these are
// captured as a list per asset key and rolled up into AssetValueDetail. Feeds
// TAA Supplemental §5's institution / account columns (joined across the list).
export interface AssetAccountEntry {
  institution: string;
  account: string;
  cashValue: string;
  interestRate: string;
}

export interface AssetsForm {
  // Financial Accounts
  checking: boolean;
  savings: boolean;
  internetBased: boolean;
  debitCards: boolean;
  moneyMarket: boolean;
  cashOnHand: boolean;
  // Investments & Insurance
  brokerage: boolean;
  capitalInvestments: boolean;
  // Stocks and Bonds are split into separate flags to match the Asset
  // Certification PDF, which lists them as distinct rows. Older application
  // data used a combined `stocksBonds` flag; new flags are independent.
  stocks: boolean;
  bonds: boolean;
  annuities: boolean;
  cds: boolean;
  cryptocurrency: boolean;
  lifeInsurance: boolean;
  trustFunds: boolean;
  // Other Assets
  lumpSumAmounts: boolean;
  safetyDepositBox: boolean;
  realEstateProperty: boolean;
  // Owner-financed real-estate receivable (distinct from `realEstateProperty`,
  // which is equity in owned real estate). Cert lists Land Contracts as its
  // own row.
  landContracts: boolean;
  taxRefund: boolean;
  // GoFundMe / online fundraising balance held by the household. NOT
  // derived from cashAssistanceFromOthers (which is the recurring-income
  // signal); this is the present-state asset balance.
  crowdsourcing: boolean;
  assetsGivenAway: boolean;
  // Collectibles, art, vehicles, etc. held for investment value rather than
  // personal use. Triggers the cert's Personal Property explanation slot.
  personalPropertyInvestment: boolean;
  otherAssetsNotListed: boolean;
  // Freeform explanations gated on their owning toggle. Both feed dedicated
  // text slots on the Asset Cert PDF (Section 1).
  personalPropertyExplanation: string;
  otherAssetExplanation: string;
  // Disposed of Asset Affidavit — captured in the dedicated `disposedAssets`
  // sub-step, rendered only when assetsGivenAway === true. Each row maps to
  // one of the 3 row slots on the PDF; explanation is required (per LIHTC
  // audit norms) and also catches overflow when a household disposed of
  // more than 3 assets.
  disposedAssets: DisposedAssetRow[];
  disposedAssetsExplanation: string;
  // Cash value + interest rate per Yes-flagged asset, keyed by the asset's
  // toggle key (e.g. "checking"). Only keys the household owns appear here.
  // `assetsGivenAway` is excluded — it's a disposal question, not a held asset.
  assetValues: Partial<Record<string, AssetValueDetail>>;
  // Per-account capture for account-bearing asset types (banks, brokerages,
  // trusts, insurers), keyed by asset toggle key. A household can list several
  // accounts of the same type; each rolls up into `assetValues[key]`. Absent for
  // non-account assets. Source of truth for the institution / account columns.
  assetAccounts: Partial<Record<string, AssetAccountEntry[]>>;
  // Federal tax refund / refundable credit received in the last 12 months.
  // HOTMA excludes a still-held federal refund from net family assets for 12
  // months (24 CFR 5.603), so `federalRefundStillHeld` is SUBTRACTED from the
  // countable total — it's already inside the checking/savings balances above,
  // not an additional asset. Captured on the otherAssets sub-step.
  federalRefundReceived: boolean | null;
  federalRefundStillHeld: string;
}

interface AssetsProps {
  defaultValues?: AssetsForm;
  onSubmit: (data: AssetsForm) => void | Promise<void>;
  onBack: () => void;
  isSaving?: boolean;
}

type AssetItemKey = Exclude<
  keyof AssetsForm,
  | "disposedAssets"
  | "disposedAssetsExplanation"
  | "personalPropertyExplanation"
  | "otherAssetExplanation"
  | "federalRefundReceived"
  | "federalRefundStillHeld"
  | "assetAccounts"
>;
type IconType = React.ComponentType<{ sx?: any }>;

interface AssetItem {
  key: AssetItemKey;
  Icon: IconType;
}

interface AssetGroup {
  groupKey:
    | "financialAccounts"
    | "investmentsInsurance"
    | "otherAssets"
    | "disposedAssets";
  items: AssetItem[];
}

const GROUPS: AssetGroup[] = [
  {
    groupKey: "financialAccounts",
    items: [
      { key: "checking", Icon: AccountBalanceWalletOutlinedIcon },
      { key: "savings", Icon: SavingsOutlinedIcon },
      { key: "internetBased", Icon: AccountBalanceWalletOutlinedIcon },
      { key: "debitCards", Icon: CreditCardIcon },
      { key: "moneyMarket", Icon: SavingsOutlinedIcon },
      { key: "cashOnHand", Icon: AttachMoneyIcon },
    ],
  },
  {
    groupKey: "investmentsInsurance",
    items: [
      { key: "brokerage", Icon: TrendingUpIcon },
      { key: "capitalInvestments", Icon: TrendingUpIcon },
      { key: "stocks", Icon: TrendingUpIcon },
      { key: "bonds", Icon: TrendingUpIcon },
      { key: "annuities", Icon: TrendingUpIcon },
      { key: "cds", Icon: SavingsOutlinedIcon },
      { key: "cryptocurrency", Icon: TrendingUpIcon },
      { key: "lifeInsurance", Icon: ShieldOutlinedIcon },
      { key: "trustFunds", Icon: AccountBalanceIcon },
    ],
  },
  {
    groupKey: "otherAssets",
    items: [
      { key: "lumpSumAmounts", Icon: AttachMoneyIcon },
      { key: "safetyDepositBox", Icon: Inventory2OutlinedIcon },
      { key: "realEstateProperty", Icon: ApartmentIcon },
      { key: "landContracts", Icon: ApartmentIcon },
      { key: "taxRefund", Icon: AttachMoneyIcon },
      { key: "crowdsourcing", Icon: AttachMoneyIcon },
      { key: "personalPropertyInvestment", Icon: Inventory2OutlinedIcon },
      { key: "assetsGivenAway", Icon: Inventory2OutlinedIcon },
      { key: "otherAssetsNotListed", Icon: HelpOutlineIcon },
    ],
  },
  // Disposed of Asset Affidavit detail capture — only renders when
  // assetsGivenAway === true. Filtered out of effectiveGroups otherwise.
  {
    groupKey: "disposedAssets",
    items: [],
  },
];

// Assets we capture a cash value + interest rate for when toggled Yes. Every
// holding toggle qualifies; `assetsGivenAway` is excluded because it asks about
// past disposals (captured separately in the disposedAssets sub-step), not a
// currently-held asset with a value.
const VALUED_ASSET_KEYS: AssetItemKey[] = GROUPS.flatMap((g) =>
  g.items.map((i) => i.key)
).filter((k) => k !== "assetsGivenAway");

const isValuedAsset = (key: AssetItemKey) => VALUED_ASSET_KEYS.includes(key);

// Interest-bearing cash/deposit assets where "annual income = cash × rate" is the
// correct model — HUD treats deposit interest as actual asset income. For these we
// REQUIRE an interest rate (0% allowed) so TAA Supplemental §5's income column can
// fill.
const DEPOSIT_ASSET_KEYS: AssetItemKey[] = [
  "checking",
  "savings",
  "moneyMarket",
  "cds",
  "internetBased",
];
const isDepositAsset = (key: AssetItemKey) => DEPOSIT_ASSET_KEYS.includes(key);

// Assets that DO produce countable income but NOT via deposit interest, so cash ×
// rate is wrong: stocks/bonds/brokerage/capital investments pay dividends or
// distributions, real estate yields net rental income. For these we capture the
// annual income explicitly (AssetValueDetail.annualIncome) and feed it into Form
// 24-W §5's stocks / real_estate income columns. Keep this set aligned with the
// TAA builder's income-bearing non-deposit rows (document-signing.ts).
const INCOME_CAPTURE_ASSET_KEYS: AssetItemKey[] = [
  "stocks",
  "bonds",
  "brokerage",
  "capitalInvestments",
  "realEstateProperty",
];
const capturesDeclaredIncome = (key: AssetItemKey) =>
  INCOME_CAPTURE_ASSET_KEYS.includes(key);

// Label for the declared-income field: real estate collects net rental income,
// everything else in the set collects dividends/distributions.
const incomeLabelKey = (key: AssetItemKey) =>
  key === "realEstateProperty"
    ? "affordable.application.assets.value.rentalIncomeLabel"
    : "affordable.application.assets.value.dividendIncomeLabel";

// Assets held at a nameable institution with an account/policy number — banks,
// brokerages, trusts, insurers. For these we capture institution + account so
// TAA §5's institution/account columns fill and the property can request
// third-party verification. Excludes assets where the pair is meaningless: cash
// on hand, real estate, crypto, lump sums, safety-deposit boxes, land contracts,
// tax refunds, crowdsourcing, personal property.
const ACCOUNT_ASSET_KEYS: AssetItemKey[] = [
  "checking",
  "savings",
  "internetBased",
  "debitCards",
  "moneyMarket",
  "brokerage",
  "capitalInvestments",
  "stocks",
  "bonds",
  "annuities",
  "cds",
  "trustFunds",
  "lifeInsurance",
];
const hasAccountFields = (key: AssetItemKey) =>
  ACCOUNT_ASSET_KEYS.includes(key);

// Currency / percent strings accept up to 2 decimals. Interest rate is required
// for deposit assets (0% allowed); optional for other valued assets. Cash value
// is always required when the asset is Yes.
const CURRENCY_RE = /^\d+(\.\d{1,2})?$/;
const PERCENT_RE = /^\d+(\.\d{1,2})?$/;

const toNum = (s?: string) => {
  const n = parseFloat((s ?? "").trim());
  return Number.isFinite(n) ? n : 0;
};

// Deposit interest = cash value x interest rate%. The correct income model ONLY
// for deposit assets and their per-account rows. Mirrors the Asset Certification's
// (A x B) column. Backend recomputes authoritatively.
const depositIncome = (detail?: {
  cashValue?: string;
  interestRate?: string;
}) => toNum(detail?.cashValue) * (toNum(detail?.interestRate) / 100);

// Single source of truth for an asset's annual income, by type:
//  - deposit assets        → cash value x interest rate (actual interest)
//  - income-capture assets → the explicitly-declared annualIncome (dividends/rent)
//  - everything else        → no countable asset income
// Backend re-derives this authoritatively; kept in sync with document-signing.ts.
const assetAnnualIncome = (key: AssetItemKey, detail?: AssetValueDetail) =>
  isDepositAsset(key)
    ? depositIncome(detail)
    : capturesDeclaredIncome(key)
    ? toNum(detail?.annualIncome)
    : 0;

const formatUsd = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

// Every asset toggle starts unanswered — same pattern as HouseholdIncome —
// so the resident must explicitly pick Yes or No. The interface keeps
// `boolean` because post-validation the value is always boolean; the null
// cast only applies to the initial unanswered state, which the toggle's
// `=== true ? "yes" : === false ? "no" : null` check renders as neither
// button highlighted.
const UNSET = null as unknown as boolean;

const DEFAULT_VALUES: AssetsForm = {
  checking: UNSET,
  savings: UNSET,
  internetBased: UNSET,
  debitCards: UNSET,
  moneyMarket: UNSET,
  cashOnHand: UNSET,
  brokerage: UNSET,
  capitalInvestments: UNSET,
  stocks: UNSET,
  bonds: UNSET,
  annuities: UNSET,
  cds: UNSET,
  cryptocurrency: UNSET,
  lifeInsurance: UNSET,
  trustFunds: UNSET,
  lumpSumAmounts: UNSET,
  safetyDepositBox: UNSET,
  realEstateProperty: UNSET,
  landContracts: UNSET,
  taxRefund: UNSET,
  crowdsourcing: UNSET,
  assetsGivenAway: UNSET,
  personalPropertyInvestment: UNSET,
  otherAssetsNotListed: UNSET,
  personalPropertyExplanation: "",
  otherAssetExplanation: "",
  disposedAssets: [],
  disposedAssetsExplanation: "",
  assetValues: {},
  assetAccounts: {},
  federalRefundReceived: UNSET,
  federalRefundStillHeld: "",
};

const EMPTY_DISPOSED_ASSET_ROW: DisposedAssetRow = {
  type: "",
  fairMarketValue: "",
  actualAmountReceived: "",
  dateOfDisposal: "",
};

const MAX_DISPOSED_ASSET_ROWS = 3;

const EMPTY_ACCOUNT: AssetAccountEntry = {
  institution: "",
  account: "",
  cashValue: "",
  interestRate: "",
};

// Bound the per-asset account list so a runaway paste can't render hundreds of
// cards. Six covers any realistic household; overflow is rare and can be noted
// to staff.
const MAX_ACCOUNTS_PER_ASSET = 6;

// The cert text limits the disposal window to "the PAST 2 YEARS" — we cap
// the date picker's min at today-2y and max at today, then enforce the same
// range in yup as a safety net (paste / autofill can bypass the picker).
const pad2 = (n: number) => String(n).padStart(2, "0");
const fmtISO = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayDate = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const twoYearsAgoDate = () => {
  const d = todayDate();
  d.setFullYear(d.getFullYear() - 2);
  return d;
};

// Repeatable per-account capture for one account-bearing asset type. A household
// can hold several accounts of the same type, so each gets its own institution /
// account / value / rate; they roll up into assetValues[key] on submit (and
// authoritatively on the server). Rendered only when the asset is toggled Yes.
// Own component so each asset key gets its own useFieldArray (hooks can't run in
// a loop). Institution + account are required; cash value required; interest
// rate required only for deposit assets (0% allowed), matching the single-asset
// rule. Reuses the module-level isDepositAsset / annualIncome / formatUsd / toNum.
const AssetAccountList: React.FC<{
  assetKey: AssetItemKey;
  control: Control<AssetsForm>;
}> = ({ assetKey, control }) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `assetAccounts.${assetKey}` as const,
  });
  const entries =
    (useWatch({ control, name: `assetAccounts.${assetKey}` as const }) as
      | AssetAccountEntry[]
      | undefined) ?? [];
  const requireRate = isDepositAsset(assetKey);
  const assetTitle = t(
    `affordable.application.assets.items.${assetKey}.title`
  ) as string;

  // Show one entry as soon as the asset is Yes, so the requirement is obvious
  // (mirrors the disposed-assets auto-append). The yup gate also enforces >=1.
  useEffect(() => {
    if (fields.length === 0) append({ ...EMPTY_ACCOUNT });
  }, [fields.length, append]);

  const subtotalCash = entries.reduce((s, e) => s + toNum(e?.cashValue), 0);
  const subtotalIncome = entries.reduce((s, e) => s + depositIncome(e), 0);

  return (
    <Box sx={{ mt: 1.5, ml: 1.75 }}>
      {fields.map((row, idx) => (
        <Box
          key={row.id}
          sx={{
            p: 2,
            mb: 1.5,
            border: `1px solid ${COLORS.gray[200]}`,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {t("affordable.application.assets.value.accountN", {
                n: idx + 1,
              })}
            </Typography>
            <IconButton
              aria-label={
                t("affordable.application.assets.value.removeAccount") as string
              }
              size="small"
              onClick={() => remove(idx)}
              disabled={fields.length <= 1}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            <Controller
              name={`assetAccounts.${assetKey}.${idx}.institution` as const}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  required
                  size="small"
                  label={t(
                    "affordable.application.assets.value.institutionLabel"
                  )}
                  placeholder={
                    t(
                      "affordable.application.assets.value.institutionPlaceholder"
                    ) as string
                  }
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{ flex: "1 1 200px" }}
                />
              )}
            />
            <Controller
              name={`assetAccounts.${assetKey}.${idx}.account` as const}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  required
                  size="small"
                  label={t("affordable.application.assets.value.accountLabel")}
                  placeholder={
                    t(
                      "affordable.application.assets.value.accountPlaceholder"
                    ) as string
                  }
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{ flex: "1 1 160px" }}
                />
              )}
            />
            <Controller
              name={`assetAccounts.${assetKey}.${idx}.cashValue` as const}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  required
                  type="number"
                  size="small"
                  label={t(
                    "affordable.application.assets.value.cashValueLabel"
                  )}
                  placeholder="0.00"
                  inputProps={{ inputMode: "decimal", min: 0, step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{ flex: "1 1 140px" }}
                />
              )}
            />
            {requireRate && (
              <Controller
                name={`assetAccounts.${assetKey}.${idx}.interestRate` as const}
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    required
                    type="number"
                    size="small"
                    label={t(
                      "affordable.application.assets.value.interestRateLabel"
                    )}
                    placeholder="0.0"
                    inputProps={{ inputMode: "decimal", min: 0, step: "0.01" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    sx={{ flex: "1 1 120px" }}
                  />
                )}
              />
            )}
          </Box>
        </Box>
      ))}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => append({ ...EMPTY_ACCOUNT })}
          disabled={fields.length >= MAX_ACCOUNTS_PER_ASSET}
        >
          {t("affordable.application.assets.value.addAccount", {
            asset: assetTitle.toLowerCase(),
          })}
        </Button>
        {subtotalCash > 0 && (
          <Typography variant="caption" color="text.secondary">
            {requireRate
              ? t("affordable.application.assets.value.accountSubtotal", {
                  cash: formatUsd(subtotalCash),
                  income: formatUsd(subtotalIncome),
                })
              : t("affordable.application.assets.value.accountSubtotalCashOnly", {
                  cash: formatUsd(subtotalCash),
                })}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const Assets: React.FC<AssetsProps> = ({
  defaultValues,
  onSubmit,
  onBack,
  isSaving = false,
}) => {
  const { t } = useTranslation();

  // Every asset Yes/No must be explicitly answered — same gate as
  // HouseholdIncome. Toggles start unselected; yup .boolean().required()
  // blocks Next until the resident picks.
  const requiredYesNoMsg = t(
    "affordable.application.assets.requiredYesNo"
  ) as string;
  const requiredYesNo = () =>
    yup.boolean().typeError(requiredYesNoMsg).required(requiredYesNoMsg);

  // Disposed of Asset Affidavit validation. Gated entirely on
  // assetsGivenAway: when false, the disposedAssets sub-step is filtered out
  // and yup imposes no constraints. When true, at least 1 row required and
  // every row's 4 fields + the explanation are required.
  const rowRequiredMsg = t(
    "affordable.application.assets.disposed.rowFieldRequired"
  ) as string;
  const schema = yup.object({
    checking: requiredYesNo(),
    savings: requiredYesNo(),
    internetBased: requiredYesNo(),
    debitCards: requiredYesNo(),
    moneyMarket: requiredYesNo(),
    cashOnHand: requiredYesNo(),
    brokerage: requiredYesNo(),
    capitalInvestments: requiredYesNo(),
    stocks: requiredYesNo(),
    bonds: requiredYesNo(),
    annuities: requiredYesNo(),
    cds: requiredYesNo(),
    cryptocurrency: requiredYesNo(),
    lifeInsurance: requiredYesNo(),
    trustFunds: requiredYesNo(),
    lumpSumAmounts: requiredYesNo(),
    safetyDepositBox: requiredYesNo(),
    realEstateProperty: requiredYesNo(),
    landContracts: requiredYesNo(),
    taxRefund: requiredYesNo(),
    crowdsourcing: requiredYesNo(),
    assetsGivenAway: requiredYesNo(),
    personalPropertyInvestment: requiredYesNo(),
    otherAssetsNotListed: requiredYesNo(),
    // Freeform explanations required when their owning toggle is Yes.
    personalPropertyExplanation: yup
      .string()
      .when("personalPropertyInvestment", {
        is: true,
        then: (s) =>
          s
            .trim()
            .required(
              t(
                "affordable.application.assets.personalPropertyExplanationRequired"
              ) as string
            ),
        otherwise: (s) => s.notRequired(),
      }),
    otherAssetExplanation: yup.string().when("otherAssetsNotListed", {
      is: true,
      then: (s) =>
        s
          .trim()
          .required(
            t(
              "affordable.application.assets.otherAssetExplanationRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    disposedAssets: yup.array().when("assetsGivenAway", {
      is: true,
      then: (s) =>
        s
          .min(
            1,
            t(
              "affordable.application.assets.disposed.atLeastOneRowRequired"
            ) as string
          )
          .max(
            MAX_DISPOSED_ASSET_ROWS,
            t(
              "affordable.application.assets.disposed.maxRowsExceeded"
            ) as string
          )
          .of(
            yup.object({
              type: yup.string().trim().required(rowRequiredMsg),
              fairMarketValue: yup
                .string()
                .matches(
                  /^\d+(\.\d{1,2})?$/,
                  t(
                    "affordable.application.assets.disposed.currencyInvalid"
                  ) as string
                )
                .required(rowRequiredMsg),
              actualAmountReceived: yup
                .string()
                .matches(
                  /^\d+(\.\d{1,2})?$/,
                  t(
                    "affordable.application.assets.disposed.currencyInvalid"
                  ) as string
                )
                .required(rowRequiredMsg),
              dateOfDisposal: yup
                .string()
                .matches(
                  /^\d{4}-\d{2}-\d{2}$/,
                  t(
                    "affordable.application.assets.disposed.dateInvalid"
                  ) as string
                )
                .test(
                  "within-2-years",
                  t(
                    "affordable.application.assets.disposed.dateOutOfRange"
                  ) as string,
                  (val) => {
                    if (!val) return true; // .required() catches empty
                    const parsed = new Date(`${val}T00:00:00`);
                    if (isNaN(parsed.getTime())) return false;
                    return parsed >= twoYearsAgoDate() && parsed <= todayDate();
                  }
                )
                .required(rowRequiredMsg),
            })
          ),
      otherwise: (s) => s.notRequired(),
    }),
    disposedAssetsExplanation: yup.string().when("assetsGivenAway", {
      is: true,
      then: (s) =>
        s
          .trim()
          .required(
            t(
              "affordable.application.assets.disposed.explanationRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    // Cash value per Yes-flagged NON-account asset (real estate, crypto, cash on
    // hand, ...). Account-bearing assets capture value per account and validate
    // via `assetAccounts` below. A single `.test` emits the error on the exact
    // nested path so react-hook-form binds it to the right input. Returns the
    // first failure; onChange re-validation surfaces the next as the user fixes.
    assetValues: yup
      .object()
      .default({})
      .test("asset-values", "", function (value) {
        const root = this.parent as Record<string, unknown>;
        const detail = (value ?? {}) as Partial<
          Record<string, AssetValueDetail>
        >;
        for (const key of VALUED_ASSET_KEYS) {
          if (root[key] !== true) continue;
          // Income-capture assets (stocks/bonds/brokerage/capital investments →
          // dividends, real estate → net rent) require an explicit annual income
          // (0 allowed) regardless of whether they're account-bearing — it lives in
          // assetValues, not the accounts list.
          if (capturesDeclaredIncome(key)) {
            const income = detail[key]?.annualIncome?.trim() ?? "";
            if (!income || !CURRENCY_RE.test(income)) {
              return this.createError({
                path: `assetValues.${key}.annualIncome`,
                message: t(
                  "affordable.application.assets.value.declaredIncomeRequired"
                ) as string,
              });
            }
          }
          if (hasAccountFields(key)) continue; // cashValue validated via assetAccounts
          const cashValue = detail[key]?.cashValue?.trim() ?? "";
          if (!cashValue || !CURRENCY_RE.test(cashValue)) {
            return this.createError({
              path: `assetValues.${key}.cashValue`,
              message: t(
                "affordable.application.assets.value.cashValueRequired"
              ) as string,
            });
          }
        }
        return true;
      }),
    // One or more accounts per Yes-flagged account-bearing asset. Each owned
    // asset needs at least one account, and every account needs an institution,
    // an account number, and a cash value — plus an interest rate for deposit
    // assets (0% allowed). Form 24-W §5 expects the institution + account, and
    // the per-account cash values roll up into the net-asset total. Errors bind
    // to the exact nested path so the right field lights up.
    assetAccounts: yup
      .object()
      .default({})
      .test("asset-accounts", "", function (value) {
        const root = this.parent as Record<string, unknown>;
        const map = (value ?? {}) as Partial<
          Record<string, AssetAccountEntry[]>
        >;
        for (const key of ACCOUNT_ASSET_KEYS) {
          if (root[key] !== true) continue;
          const list = map[key] ?? [];
          if (list.length === 0) {
            return this.createError({
              path: `assetAccounts.${key}.0.institution`,
              message: t(
                "affordable.application.assets.value.institutionRequired"
              ) as string,
            });
          }
          for (let i = 0; i < list.length; i++) {
            const e = list[i];
            if (!(e?.institution ?? "").trim()) {
              return this.createError({
                path: `assetAccounts.${key}.${i}.institution`,
                message: t(
                  "affordable.application.assets.value.institutionRequired"
                ) as string,
              });
            }
            if (!(e?.account ?? "").trim()) {
              return this.createError({
                path: `assetAccounts.${key}.${i}.account`,
                message: t(
                  "affordable.application.assets.value.accountRequired"
                ) as string,
              });
            }
            const cash = (e?.cashValue ?? "").trim();
            if (!cash || !CURRENCY_RE.test(cash)) {
              return this.createError({
                path: `assetAccounts.${key}.${i}.cashValue`,
                message: t(
                  "affordable.application.assets.value.cashValueRequired"
                ) as string,
              });
            }
            const rate = (e?.interestRate ?? "").trim();
            if (isDepositAsset(key) && !rate) {
              return this.createError({
                path: `assetAccounts.${key}.${i}.interestRate`,
                message: t(
                  "affordable.application.assets.value.interestRateRequired"
                ) as string,
              });
            }
            if (rate && !PERCENT_RE.test(rate)) {
              return this.createError({
                path: `assetAccounts.${key}.${i}.interestRate`,
                message: t(
                  "affordable.application.assets.value.interestRateInvalid"
                ) as string,
              });
            }
          }
        }
        return true;
      }),
    // Federal tax refund: must answer Yes/No; if Yes, the still-held amount is
    // required (0 allowed) so we can subtract it from countable net assets.
    federalRefundReceived: yup
      .boolean()
      .nullable()
      .test(
        "refund-answered",
        t("affordable.application.assets.refund.receivedRequired") as string,
        (v) => v === true || v === false
      ),
    federalRefundStillHeld: yup.string().when("federalRefundReceived", {
      is: true,
      then: (s) =>
        s
          .trim()
          .required(
            t("affordable.application.assets.refund.amountRequired") as string
          )
          .matches(CURRENCY_RE, {
            message: t(
              "affordable.application.assets.refund.amountInvalid"
            ) as string,
          }),
      otherwise: (s) => s.notRequired(),
    }),
  });

  const { control, handleSubmit, watch, trigger } = useForm<AssetsForm>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    defaultValues: defaultValues || DEFAULT_VALUES,
  });

  const assetsGivenAway = watch("assetsGivenAway");
  const {
    fields: disposedRows,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "disposedAssets",
  });

  // Filter `disposedAssets` sub-step out when not applicable, same pattern
  // as HouseholdIncome's effectiveGroups / cashTip filter.
  const effectiveGroups = useMemo(() => {
    let g = GROUPS;
    if (!assetsGivenAway) {
      g = g.filter((x) => x.groupKey !== "disposedAssets");
    }
    return g;
  }, [assetsGivenAway]);

  const [subStep, setSubStep] = useState(0);
  // Reset subStep if the active group disappeared from effectiveGroups
  // (e.g. user toggled assetsGivenAway off after landing on disposedAssets).
  const safeSubStep = Math.min(subStep, effectiveGroups.length - 1);
  const isFirstSubStep = safeSubStep === 0;
  const isLastSubStep = safeSubStep === effectiveGroups.length - 1;
  const currentGroup = effectiveGroups[safeSubStep];

  // Auto-append one empty row when the user lands on the disposedAssets
  // sub-step with no rows. The yup .min(1) gate would otherwise produce a
  // silent failure on Next (its error isn't bound to any rendered field),
  // and the bare "+ Add" button reads as optional. Showing the row form up
  // front makes the requirement obvious and matches the cert's expectation
  // (at least one row).
  useEffect(() => {
    if (
      currentGroup?.groupKey === "disposedAssets" &&
      disposedRows.length === 0
    ) {
      append(EMPTY_DISPOSED_ASSET_ROW);
    }
  }, [currentGroup?.groupKey, disposedRows.length, append]);

  const goBack = () => {
    if (isFirstSubStep) onBack();
    else setSubStep(safeSubStep - 1);
  };

  // Watch toggles that reveal conditional fields so the explanation inputs
  // render reactively in the otherAssets sub-step.
  const personalPropertyInvestment = watch("personalPropertyInvestment");
  const otherAssetsNotListed = watch("otherAssetsNotListed");

  // Running net-family-assets total: sum the cash value of every Yes-flagged
  // asset. Drives the under/over HOTMA-threshold banner (and downstream, the
  // self-cert vs. full-verification document path). Recomputes live as the
  // resident types. `watch()` (no args) subscribes to all field changes.
  const allValues = watch();
  const assetTotal = VALUED_ASSET_KEYS.reduce((sum, key) => {
    if ((allValues as unknown as Record<string, unknown>)[key] !== true)
      return sum;
    // Account-bearing assets sum across their captured accounts; non-account
    // assets read the single cash value.
    if (hasAccountFields(key)) {
      const list = allValues.assetAccounts?.[key] ?? [];
      return sum + list.reduce((s, e) => s + toNum(e?.cashValue), 0);
    }
    return sum + toNum(allValues.assetValues?.[key]?.cashValue);
  }, 0);
  // HOTMA: a still-held federal refund is excluded from net family assets for
  // 12 months. It already sits inside the balances above, so subtract it to get
  // the countable total used for the threshold (24 CFR 5.603).
  const heldRefund =
    allValues.federalRefundReceived === true
      ? toNum(allValues.federalRefundStillHeld)
      : 0;
  const countableTotal = Math.max(0, assetTotal - heldRefund);
  const overThreshold = countableTotal > configs.HOTMA_ASSET_THRESHOLD;
  const isAssetGroup = currentGroup?.groupKey !== "disposedAssets";
  const thresholdLabel = configs.HOTMA_ASSET_THRESHOLD.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

  const goNext = async () => {
    // Asset sub-steps (financialAccounts / investmentsInsurance / otherAssets):
    // every Yes/No toggle on the screen must be answered before advancing.
    // For otherAssets, the conditional explanations are also triggered when
    // their owning toggle is Yes.
    if (currentGroup.groupKey !== "disposedAssets") {
      const fields: (keyof AssetsForm)[] = currentGroup.items.map((i) => i.key);
      if (
        currentGroup.groupKey === "otherAssets" &&
        personalPropertyInvestment === true
      ) {
        fields.push("personalPropertyExplanation");
      }
      if (
        currentGroup.groupKey === "otherAssets" &&
        otherAssetsNotListed === true
      ) {
        fields.push("otherAssetExplanation");
      }
      // The federal-refund question lives on the otherAssets sub-step.
      if (currentGroup.groupKey === "otherAssets") {
        fields.push("federalRefundReceived", "federalRefundStillHeld");
      }
      // Also validate the value/rate inputs revealed by this sub-step's
      // Yes-flagged assets: assetValues (non-account) + assetAccounts (per-account).
      fields.push("assetValues", "assetAccounts");
      const ok = await trigger(fields);
      if (!ok) return;
    }
    if (currentGroup.groupKey === "disposedAssets") {
      const ok = await trigger(["disposedAssets", "disposedAssetsExplanation"]);
      if (!ok) return;
    }
    if (isLastSubStep) handleSubmit(submitWithRollups)();
    else setSubStep(safeSubStep + 1);
  };

  // Before submit, derive the assetValues rollup for every account-bearing asset
  // from its captured accounts: cashValue = Σ, interestRate = cash-weighted
  // blended rate (full precision), institution / account = distinct joined. The
  // server re-derives this authoritatively on save (normalizeDeclaredAssetAccounts),
  // but writing it client-side keeps the saved blob consistent for any consumer
  // that reads assetValues before a refetch (FinancialSummary, documentRequirements).
  const submitWithRollups = (data: AssetsForm) => {
    const assetValues: Partial<Record<string, AssetValueDetail>> = {
      ...data.assetValues,
    };
    for (const key of ACCOUNT_ASSET_KEYS) {
      if (data[key] !== true) continue;
      const list = data.assetAccounts?.[key] ?? [];
      let cashSum = 0;
      let incomeSum = 0;
      const institutions = new Set<string>();
      const accounts = new Set<string>();
      for (const e of list) {
        const cv = toNum(e?.cashValue);
        cashSum += cv;
        incomeSum += depositIncome(e);
        const inst = (e?.institution ?? "").trim();
        if (inst) institutions.add(inst);
        const acct = (e?.account ?? "").trim();
        if (acct) accounts.add(acct);
      }
      const blendedRatePct = cashSum > 0 ? (incomeSum / cashSum) * 100 : 0;
      assetValues[key] = {
        cashValue: cashSum > 0 ? String(cashSum) : "",
        interestRate: String(blendedRatePct),
        institution: Array.from(institutions).join("; "),
        account: Array.from(accounts).join("; "),
        // Per-account rollup never sets income for income-capture assets (dividends/
        // rent aren't cash x rate); preserve the asset-level declared figure the
        // resident typed below the accounts list.
        annualIncome: data.assetValues?.[key]?.annualIncome,
      };
    }
    return onSubmit({ ...data, assetValues });
  };

  const renderRow = (itemKey: AssetItemKey, Icon: IconType) => (
    <Controller
      key={itemKey}
      name={itemKey}
      control={control}
      render={({ field, fieldState }) => {
        const isYes = field.value === true;
        const hasError = !!fieldState.error;
        const rowDetail = watch(`assetValues.${itemKey}`) as
          | AssetValueDetail
          | undefined;
        return (
          <Box sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                border: `1px solid ${
                  hasError
                    ? COLORS.error.main
                    : isYes
                    ? COLORS.darkBlue[500]
                    : COLORS.gray[200]
                }`,
                borderRadius: 2,
                backgroundColor: isYes ? COLORS.blue[50] : "transparent",
                transition: "all 0.2s ease",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: COLORS.gray[100],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ color: COLORS.gray[600], fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {t(`affordable.application.assets.items.${itemKey}.title`)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.25 }}
                >
                  {t(
                    `affordable.application.assets.items.${itemKey}.description`
                  )}
                </Typography>
              </Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={
                  field.value === true
                    ? "yes"
                    : field.value === false
                    ? "no"
                    : null
                }
                onChange={(_, val) => {
                  if (val !== null) field.onChange(val === "yes");
                }}
                sx={{ flexShrink: 0 }}
              >
                <ToggleButton value="yes" sx={{ px: 2 }}>
                  {t("affordable.application.assets.yes")}
                </ToggleButton>
                <ToggleButton value="no" sx={{ px: 2 }}>
                  {t("affordable.application.assets.no")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {isYes && isValuedAsset(itemKey) && !hasAccountFields(itemKey) && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 1.5,
                  ml: 1.75,
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                }}
              >
                <Controller
                  name={`assetValues.${itemKey}.cashValue` as const}
                  control={control}
                  render={({ field: vField, fieldState: vState }) => (
                    <TextField
                      {...vField}
                      value={vField.value ?? ""}
                      required
                      type="number"
                      size="small"
                      label={t("affordable.application.assets.value.cashValueLabel")}
                      placeholder="0.00"
                      inputProps={{ inputMode: "decimal", min: 0, step: "0.01" }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      error={!!vState.error}
                      helperText={vState.error?.message}
                      sx={{ flex: "1 1 160px" }}
                    />
                  )}
                />
                {capturesDeclaredIncome(itemKey) ? (
                  // Income-capture non-account asset (real estate): collect the
                  // annual income directly — net rent isn't cash x rate.
                  <Controller
                    name={`assetValues.${itemKey}.annualIncome` as const}
                    control={control}
                    render={({ field: iField, fieldState: iState }) => (
                      <TextField
                        {...iField}
                        value={iField.value ?? ""}
                        required
                        type="number"
                        size="small"
                        label={t(incomeLabelKey(itemKey))}
                        placeholder="0.00"
                        inputProps={{ inputMode: "decimal", min: 0, step: "0.01" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                        error={!!iState.error}
                        helperText={iState.error?.message}
                        sx={{ flex: "1 1 200px" }}
                      />
                    )}
                  />
                ) : (
                  <>
                    <Controller
                      name={`assetValues.${itemKey}.interestRate` as const}
                      control={control}
                      render={({ field: rField, fieldState: rState }) => (
                        <TextField
                          {...rField}
                          value={rField.value ?? ""}
                          required={isDepositAsset(itemKey)}
                          type="number"
                          size="small"
                          label={t(
                            "affordable.application.assets.value.interestRateLabel"
                          )}
                          placeholder="0.0"
                          inputProps={{
                            inputMode: "decimal",
                            min: 0,
                            step: "0.01",
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          }}
                          error={!!rState.error}
                          helperText={rState.error?.message}
                          sx={{ flex: "1 1 120px" }}
                        />
                      )}
                    />
                    <Box
                      sx={{
                        flex: "1 1 140px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        minHeight: 40,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: COLORS.darkBlue[500], fontWeight: 600 }}
                      >
                        {t(
                          "affordable.application.assets.value.annualIncomeLabel"
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatUsd(assetAnnualIncome(itemKey, rowDetail))}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}
            {isYes && hasAccountFields(itemKey) && (
              <AssetAccountList assetKey={itemKey} control={control} />
            )}
            {isYes &&
              hasAccountFields(itemKey) &&
              capturesDeclaredIncome(itemKey) && (
                // Dividends/distributions for stocks/bonds/brokerage/capital
                // investments: one figure per asset type (not per account), feeding
                // Form 24-W §5's income column. Cash value comes from the accounts
                // above; this is the income those holdings produced.
                <Box sx={{ mt: 1.5, ml: 1.75 }}>
                  <Controller
                    name={`assetValues.${itemKey}.annualIncome` as const}
                    control={control}
                    render={({ field: iField, fieldState: iState }) => (
                      <TextField
                        {...iField}
                        value={iField.value ?? ""}
                        required
                        type="number"
                        size="small"
                        label={t(incomeLabelKey(itemKey))}
                        placeholder="0.00"
                        inputProps={{ inputMode: "decimal", min: 0, step: "0.01" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                        error={!!iState.error}
                        helperText={
                          iState.error?.message ??
                          (t(
                            "affordable.application.assets.value.dividendIncomeHelper"
                          ) as string)
                        }
                        sx={{ flex: "1 1 200px", maxWidth: 280 }}
                      />
                    )}
                  />
                </Box>
              )}
            {hasError && (
              <Typography
                variant="caption"
                sx={{
                  color: COLORS.error.main,
                  mt: 0.5,
                  ml: 1.75,
                  display: "block",
                }}
              >
                {fieldState.error?.message}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );

  return (
    <Box component="form" onSubmit={(e) => e.preventDefault()}>
      <RegisterReviewSection>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            mb: 0.5,
          }}
        >
          <RegisterReviewSectionTitle sx={{ mb: 0 }}>
            {t(
              `affordable.application.assets.groups.${currentGroup.groupKey}.title`
            )}
          </RegisterReviewSectionTitle>
          <Typography variant="body2" color="text.secondary">
            {safeSubStep + 1} / {effectiveGroups.length}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            `affordable.application.assets.groups.${currentGroup.groupKey}.subtitle`
          )}
        </Typography>
        {currentGroup.items.map((item) => renderRow(item.key, item.Icon))}

        {currentGroup.groupKey === "otherAssets" &&
          personalPropertyInvestment === true && (
            <Box sx={{ mt: 1.5, mb: 1.5 }}>
              <Controller
                name="personalPropertyExplanation"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    multiline
                    minRows={2}
                    label={t(
                      "affordable.application.assets.personalPropertyExplanationLabel"
                    )}
                    placeholder={
                      t(
                        "affordable.application.assets.personalPropertyExplanationPlaceholder"
                      ) as string
                    }
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          )}

        {currentGroup.groupKey === "otherAssets" &&
          otherAssetsNotListed === true && (
            <Box sx={{ mt: 1.5, mb: 1.5 }}>
              <Controller
                name="otherAssetExplanation"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    multiline
                    minRows={2}
                    label={t(
                      "affordable.application.assets.otherAssetExplanationLabel"
                    )}
                    placeholder={
                      t(
                        "affordable.application.assets.otherAssetExplanationPlaceholder"
                      ) as string
                    }
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          )}

        {currentGroup.groupKey === "otherAssets" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t("affordable.application.assets.refund.title")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1 }}
              >
                {t("affordable.application.assets.refund.question")}
              </Typography>
              <Controller
                name="federalRefundReceived"
                control={control}
                render={({ field, fieldState }) => (
                  <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={
                        field.value === true
                          ? "yes"
                          : field.value === false
                          ? "no"
                          : null
                      }
                      onChange={(_, val) => {
                        if (val !== null) field.onChange(val === "yes");
                      }}
                    >
                      <ToggleButton value="yes" sx={{ px: 2 }}>
                        {t("affordable.application.assets.yes")}
                      </ToggleButton>
                      <ToggleButton value="no" sx={{ px: 2 }}>
                        {t("affordable.application.assets.no")}
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {fieldState.error && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Box>
            {allValues.federalRefundReceived === true && (
              <Box sx={{ mt: 1.5 }}>
                <Controller
                  name="federalRefundStillHeld"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ""}
                      required
                      type="number"
                      size="small"
                      label={t(
                        "affordable.application.assets.refund.amountLabel"
                      )}
                      placeholder="0.00"
                      inputProps={{ inputMode: "decimal", min: 0, step: "0.01" }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ??
                        t("affordable.application.assets.refund.amountHelper")
                      }
                      sx={{ maxWidth: 320 }}
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        )}

        {currentGroup.groupKey === "disposedAssets" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {disposedRows.map((row, idx) => (
              <Box
                key={row.id}
                sx={{
                  p: 2,
                  border: `1px solid ${COLORS.gray[200]}`,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {t("affordable.application.assets.disposed.rowLabel", {
                      n: idx + 1,
                    })}
                  </Typography>
                  <IconButton
                    aria-label={
                      t(
                        "affordable.application.assets.disposed.removeRow"
                      ) as string
                    }
                    size="small"
                    onClick={() => remove(idx)}
                    disabled={disposedRows.length <= 1}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Controller
                  name={`disposedAssets.${idx}.type` as const}
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label={t(
                        "affordable.application.assets.disposed.typeLabel"
                      )}
                      placeholder={
                        t(
                          "affordable.application.assets.disposed.typePlaceholder"
                        ) as string
                      }
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name={`disposedAssets.${idx}.fairMarketValue` as const}
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      type="number"
                      label={t(
                        "affordable.application.assets.disposed.fairMarketValueLabel"
                      )}
                      placeholder="0.00"
                      inputProps={{
                        inputMode: "decimal",
                        min: 0,
                        step: "0.01",
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name={`disposedAssets.${idx}.actualAmountReceived` as const}
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      type="number"
                      label={t(
                        "affordable.application.assets.disposed.actualAmountReceivedLabel"
                      )}
                      placeholder="0.00"
                      inputProps={{
                        inputMode: "decimal",
                        min: 0,
                        step: "0.01",
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name={`disposedAssets.${idx}.dateOfDisposal` as const}
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      type="date"
                      label={t(
                        "affordable.application.assets.disposed.dateOfDisposalLabel"
                      )}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: fmtISO(twoYearsAgoDate()),
                        max: fmtISO(todayDate()),
                      }}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ??
                        (t(
                          "affordable.application.assets.disposed.dateOfDisposalHelper"
                        ) as string)
                      }
                    />
                  )}
                />
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => append(EMPTY_DISPOSED_ASSET_ROW)}
              disabled={disposedRows.length >= MAX_DISPOSED_ASSET_ROWS}
            >
              {disposedRows.length === 0
                ? t("affordable.application.assets.disposed.addFirstRow")
                : t("affordable.application.assets.disposed.addAnotherRow", {
                    remaining: MAX_DISPOSED_ASSET_ROWS - disposedRows.length,
                  })}
            </Button>
            <Controller
              name="disposedAssetsExplanation"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  multiline
                  minRows={3}
                  label={t(
                    "affordable.application.assets.disposed.explanationLabel"
                  )}
                  placeholder={
                    t(
                      "affordable.application.assets.disposed.explanationPlaceholder"
                    ) as string
                  }
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Box>
        )}

        {isAssetGroup && assetTotal > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              border: `1px solid ${
                overThreshold ? COLORS.warning.main : COLORS.success.main
              }`,
              backgroundColor: overThreshold
                ? `${COLORS.warning.main}14`
                : `${COLORS.success.main}14`,
            }}
          >
            {heldRefund > 0 ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("affordable.application.assets.refund.grossLabel")}
                  </Typography>
                  <Typography variant="body2">
                    {formatUsd(assetTotal)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("affordable.application.assets.refund.lessRefundLabel")}
                  </Typography>
                  <Typography variant="body2">
                    {"− "}
                    {formatUsd(heldRefund)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 2,
                    mt: 0.5,
                    pt: 0.5,
                    borderTop: `1px solid ${COLORS.gray[200]}`,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("affordable.application.assets.refund.countableLabel")}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {formatUsd(countableTotal)}
                  </Typography>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("affordable.application.assets.value.runningTotalLabel")}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {formatUsd(assetTotal)}
                </Typography>
              </Box>
            )}
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.5,
                color: overThreshold
                  ? COLORS.warning.main
                  : COLORS.success.main,
                fontWeight: 600,
              }}
            >
              {overThreshold
                ? t("affordable.application.assets.value.overThreshold", {
                    threshold: thresholdLabel,
                  })
                : t("affordable.application.assets.value.underThreshold", {
                    threshold: thresholdLabel,
                  })}
            </Typography>
          </Box>
        )}
      </RegisterReviewSection>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
          mb: 4,
        }}
      >
        <Button variant="outlined" onClick={goBack} disabled={isSaving}>
          {t("account.leaseApplication.previous")}
        </Button>
        <Button
          variant="contained"
          onClick={goNext}
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </Box>
  );
};
